import { Provider } from '@angular/core';
import { TypedClassProvider } from '@shared/ioc/typed-class-provider';
import { EgiAppStoreActions } from '@/app/platform/platform-data/actions/egi-app-store.actions';
import {
  ANNOUNCEMENT_SERVICE,
  DEVICE_SERVICE,
  EGI_APP_SERVICE,
  NAVIGATION_ITEM_SERVICE,
  PRESENT_DEVICE_USER_SERVICE,
  USER_ACCOUNT_SERVICE,
  USER_GROUP_SERVICE
} from '@/app/platform/platform-data/services/types';
import {
  DEVICE_QUERIES,
  EGI_APP_QUERIES,
  PRESENT_DEVICE_USER_QUERIES,
  USER_ACCOUNT_QUERIES,
  USER_GROUP_QUERIES
} from '@/app/platform/platform-data/queries/types';
import { UserGroupStoreActions } from '@/app/platform/platform-data/actions/user-group-store.actions';
import { EGI_APP_SYNCHRONIZER } from '@/app/platform/platform-data/synchronizers/types';
import { ApiEgiAppSynchronizer } from '@/app/platform/platform-data/synchronizers/api-egi-app-synchronizer';
import {
  AUTHORIZATION_ACTIONS,
  DEVICE_ACTIONS,
  EGI_APP_ACTIONS,
  NAVIGATION_ITEM_ACTIONS,
  PRESENT_DEVICE_USER_ACTIONS,
  USER_ACCOUNT_ACTIONS,
  USER_GROUP_ACTIONS
} from '@/app/platform/platform-data/actions/types';
import { GroupAuthorizationStoreActions } from '@/app/platform/platform-data/actions/group-authorization-store.actions';
import { UserGroupStoreQueries } from '@/app/platform/platform-data/queries/user-group-store-queries';
import { UserAccountStoreQueries } from '@/app/platform/platform-data/queries/user-account-store.queries';
import { UserAccountStoreActions } from '@/app/platform/platform-data/actions/user-account-store.actions';
import { NavigationItemStoreActions } from '@/app/platform/platform-data/actions/navigation-item-store.actions';
import { DeviceFacade } from '@/app/platform/platform-data/services/device-facade';
import { DeviceStoreActions } from '@/app/platform/platform-data/actions/device-store.actions';
import { DeviceStoreQueries } from '@/app/platform/platform-data/queries/device-store.queries';
import { EgiAppFacade } from '@/app/platform/platform-data/services/egi-app-facade';
import { NavigationItemMapFacade } from '@/app/platform/platform-data/services/navigation-item-map-facade';
import { EgiAppStoreQueries } from '@/app/platform/platform-data/queries/egi-app-store.queries';
import { PresentDeviceUserStoreActions } from '@/app/platform/platform-data/actions/present-device-user-store.actions';
import { PresentDeviceUserStoreQueries } from '@/app/platform/platform-data/queries/present-device-user-store-queries';
import { NavigationItemDataEnricher } from '@/app/platform/platform-shared/enrichers/navigation-item-data-enricher';
import { UserGroupFacade } from '@/app/platform/platform-data/services/user-group.facade';
import { UserAccountFacade } from '@/app/platform/platform-data/services/user-account.facade';
import { PresentDeviceUserApiFacade } from '@/app/platform/platform-data/services/present-device-user-api.facade';
import { AutoResetAfterLogoutProvider } from '@core/guards/clear-stores-after-logout.guard';
import { DeviceEntityStore } from '@/app/platform/platform-data/store/device.entity.store';
import { EgiAppEntityStore } from '@/app/platform/platform-data/store/egi-app.entity.store';
import { PresentDeviceUserStore } from '@/app/platform/platform-data/store/present-device-user.store';
import { UserAccountEntityStore } from '@/app/platform/platform-data/store/user-account.entity.store';
import { UserGroupEntityStore } from '@/app/platform/platform-data/store/user-group.entity.store';
import { AnnouncementApiService } from '@/app/platform/platform-data/services/announcement-api.service';

const storeProviders: Provider[] = [
  new TypedClassProvider(USER_GROUP_ACTIONS, UserGroupStoreActions),
  new TypedClassProvider(USER_GROUP_QUERIES, UserGroupStoreQueries),
  new TypedClassProvider(AUTHORIZATION_ACTIONS, GroupAuthorizationStoreActions),
  new TypedClassProvider(USER_ACCOUNT_QUERIES, UserAccountStoreQueries),
  new TypedClassProvider(USER_ACCOUNT_ACTIONS, UserAccountStoreActions),
  new TypedClassProvider(DEVICE_ACTIONS, DeviceStoreActions),
  new TypedClassProvider(DEVICE_QUERIES, DeviceStoreQueries),
  new TypedClassProvider(EGI_APP_QUERIES, EgiAppStoreQueries),
  new TypedClassProvider(NAVIGATION_ITEM_ACTIONS, NavigationItemStoreActions),
  new TypedClassProvider(EGI_APP_ACTIONS, EgiAppStoreActions),
  new TypedClassProvider(PRESENT_DEVICE_USER_ACTIONS, PresentDeviceUserStoreActions),
  new TypedClassProvider(PRESENT_DEVICE_USER_QUERIES, PresentDeviceUserStoreQueries),
  new AutoResetAfterLogoutProvider(DeviceEntityStore),
  new AutoResetAfterLogoutProvider(EgiAppEntityStore),
  new AutoResetAfterLogoutProvider(PresentDeviceUserStore),
  new AutoResetAfterLogoutProvider(UserAccountEntityStore),
  new AutoResetAfterLogoutProvider(UserGroupEntityStore)
];

const serviceProviders: Provider[] = [
  new TypedClassProvider(USER_GROUP_SERVICE, UserGroupFacade),
  new TypedClassProvider(USER_ACCOUNT_SERVICE, UserAccountFacade),
  new TypedClassProvider(DEVICE_SERVICE, DeviceFacade),
  new TypedClassProvider(EGI_APP_SERVICE, EgiAppFacade),
  new TypedClassProvider(NAVIGATION_ITEM_SERVICE, NavigationItemMapFacade),
  new TypedClassProvider(PRESENT_DEVICE_USER_SERVICE, PresentDeviceUserApiFacade),
  new TypedClassProvider(ANNOUNCEMENT_SERVICE, AnnouncementApiService)
];

export const platformDataProviders: Provider[] = [
  ...storeProviders,
  ...serviceProviders,
  NavigationItemDataEnricher,
  new TypedClassProvider(EGI_APP_SYNCHRONIZER, ApiEgiAppSynchronizer)
];
